import { Dayjs, duration } from 'dayjs'
import { Duration } from 'dayjs/plugin/duration'

import { emptyDuration, OpeningHours as GanttOpeningHours, string_ToTimeString } from '~/astra'
import { getFlag } from '~/hooks/useFlag'
import { Location, LocationServiceOverride } from '~/storm'
import { ClosedReason } from '~/utils/locationServiceOverride'
import { findServicePeriod, ServicePeriod } from '~/utils/servicePeriods'

/**
 * Whether a location is open on a given day, mirroring the opening hours module's `OpeningHoursStatus`.
 * Only open days have hours. A day not covered by a service period is `not_configured`: like a closed
 * day it can't be planned on, but nobody closed it, so it can't be reopened and is fixed by setting up
 * opening hours in Sykehusoppsett. Restrict on `status !== 'open'`; `isClosed` is only for closures.
 */
export type LocationDayOpeningStatus = LocationDayOpen | LocationDayClosed | LocationDayNotConfigured

export type LocationDayOpen = { status: 'open'; isClosed: false } & OpeningWindow & OpeningDurations

export type LocationDayClosed = { status: 'closed'; isClosed: true; closedReason: ClosedReason }

export type LocationDayNotConfigured = { status: 'not_configured'; isClosed: false }

/** A location-day that can't be planned on, whether someone closed it or no service period covers it. */
export type LocationDayNotOpen = LocationDayClosed | LocationDayNotConfigured

type OpeningWindow = { open: string; close: string }

type OpeningDurations = {
    duration: Duration
    changeDuration: Duration
    lunchDuration: Duration
}

function getClosedStatus({
    locationServiceOverride,
    servicePeriod,
}: {
    locationServiceOverride: LocationServiceOverride | undefined
    servicePeriod: ServicePeriod | undefined
}): LocationDayClosed | undefined {
    // Location service overrides take precedence over service periods
    if (locationServiceOverride) {
        return { status: 'closed', isClosed: true, closedReason: locationServiceOverride.closed_reason }
    }

    if (servicePeriod?.is_closed) {
        return { status: 'closed', isClosed: true, closedReason: 'service_period' }
    }

    return undefined
}

// Default weekday hours for locations without a service period. Kept until the opening hours module
// is enabled (use_location_opening_hours_endpoints), as some tenants rely on it until their service
// periods are backfilled.
const isoMonday = 1
const isoTuesday = isoMonday + 1
const isoWednesday = isoTuesday + 1
const isoThursday = isoWednesday + 1
const isoFriday = isoThursday + 1

function getMockOpeningWindow(date: Dayjs, _location: Location): OpeningWindow | undefined {
    if (
        date.isoWeekday() === isoMonday ||
        date.isoWeekday() === isoTuesday ||
        date.isoWeekday() === isoWednesday ||
        date.isoWeekday() === isoThursday ||
        date.isoWeekday() === isoFriday
    ) {
        return { open: '8:00', close: '15:30' }
    }
}

/**
 * Decides how a location-day without a service period should be interpreted: default opening hours, or none
 * (`not_configured`).
 *
 * Controlled by `use_location_opening_hours_endpoints`:
 * - Off: default weekday hours from `getMockOpeningWindow`, for tenants whose service periods are not
 *   backfilled yet
 * - On: no hours, so the day is `not_configured`
 *
 * The switch is interim:
 * - It stands in until opening hours are fetched from the location opening hours module
 * - After that, the default hours still apply while the flag is off
 * - Once the flag is enabled for good and nothing relies on default hours, remove `getMockOpeningWindow`
 */
function getFallbackOpeningWindow(date: Dayjs, location: Location): OpeningWindow | undefined {
    if (getFlag('use_location_opening_hours_endpoints')) return undefined

    return getMockOpeningWindow(date, location)
}

function getOpeningWindowDuration(openingWindow: OpeningWindow) {
    const [openingHour, openingMinute] = openingWindow.open.split(':')
    const [closingHour, closingMinute] = openingWindow.close.split(':')

    const duration_h = Number(closingHour) - Number(openingHour) + (Number(closingMinute) - Number(openingMinute)) / 60
    return duration({ hours: duration_h })
}

function toDurationOrEmptyDuration(isoDuration: string | null | undefined): Duration {
    return isoDuration ? duration(isoDuration) : emptyDuration
}

function getOpeningDurations(openingWindow: OpeningWindow, servicePeriod: ServicePeriod | undefined): OpeningDurations {
    return {
        duration: getOpeningWindowDuration(openingWindow),
        changeDuration: toDurationOrEmptyDuration(servicePeriod?.change_duration),
        lunchDuration: toDurationOrEmptyDuration(servicePeriod?.lunch_duration),
    }
}

export function getOpeningHours({
    date,
    location,
    locationServiceOverride,
}: {
    date: Dayjs
    location: Location
    locationServiceOverride: LocationServiceOverride | undefined
}): LocationDayOpeningStatus {
    const servicePeriod = findServicePeriod({ date, location })

    const closedStatus = getClosedStatus({ locationServiceOverride, servicePeriod })

    if (closedStatus) {
        return closedStatus
    }

    const openingWindow = servicePeriod ? { open: servicePeriod.open, close: servicePeriod.close } : getFallbackOpeningWindow(date, location)

    if (openingWindow === undefined) {
        return { status: 'not_configured', isClosed: false }
    }
    return { ...openingWindow, ...getOpeningDurations(openingWindow, servicePeriod), status: 'open', isClosed: false }
}

/**
 * Maps an OR-day's opening window from the API (`HH:MM:SS`, or `null` when unknown) to a Gantt row's opening hours.
 * A missing or zero-length window means the day's opening hours are unknown, so the row is left unhatched.
 */
export function toGanttOpeningHours(openTime: string | null | undefined, closeTime: string | null | undefined): GanttOpeningHours | undefined {
    if (!openTime || !closeTime || openTime === closeTime) return undefined

    return { open: string_ToTimeString(openTime), close: string_ToTimeString(closeTime) }
}
